/**
 * Risk Matrix Page - Sprint 12
 * 5x5 Heat Matrix visualization with CRUD and export functionality
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  AlertTriangle, Plus, Download, Filter, RefreshCw, Edit2, Trash2,
  ChevronDown, X, Save, User, Calendar, Loader2, FileText, Image, Table
} from 'lucide-react';
import { toast } from 'sonner';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Severity colors
const SEVERITY_COLORS = {
  critical: { bg: '#dc2626', text: '#fff', label: 'Critical' },
  high: { bg: '#f97316', text: '#fff', label: 'High' },
  medium: { bg: '#facc15', text: '#000', label: 'Medium' },
  low: { bg: '#22c55e', text: '#fff', label: 'Low' }
};

// Categories
const CATEGORIES = ['technical', 'schedule', 'resource', 'budget', 'scope', 'external', 'other'];
const STATUSES = ['open', 'mitigated', 'closed', 'accepted'];
const MITIGATION_STATUSES = ['not_started', 'in_progress', 'completed', 'accepted'];

// Helper to get severity color
const getSeverityColor = (likelihood, impact) => {
  const score = likelihood * impact;
  if (score >= 16) return SEVERITY_COLORS.critical;
  if (score >= 9) return SEVERITY_COLORS.high;
  if (score >= 4) return SEVERITY_COLORS.medium;
  return SEVERITY_COLORS.low;
};

// Matrix Cell Component
const MatrixCell = ({ likelihood, impact, risks, onClick, isSelected }) => {
  const color = getSeverityColor(likelihood, impact);
  const count = risks?.length || 0;
  
  return (
    <div 
      className={`relative w-full h-20 flex items-center justify-center rounded-lg cursor-pointer transition-all ${
        isSelected ? 'ring-4 ring-blue-500 ring-offset-2' : 'hover:ring-2 hover:ring-blue-300'
      }`}
      style={{ backgroundColor: color.bg }}
      onClick={() => onClick(likelihood, impact, risks)}
      data-testid={`matrix-cell-${likelihood}-${impact}`}
    >
      {count > 0 && (
        <div className="text-center" style={{ color: color.text }}>
          <div className="text-2xl font-bold">{count}</div>
          <div className="text-xs opacity-80">risk{count !== 1 ? 's' : ''}</div>
        </div>
      )}
      {count === 0 && (
        <div className="text-xs opacity-50" style={{ color: color.text }}>-</div>
      )}
    </div>
  );
};

// Risk Drawer Component
const RiskDrawer = ({ isOpen, onClose, risk, onSave, onDelete }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'other',
    likelihood: 3,
    impact: 3,
    mitigation_plan: '',
    mitigation_status: 'not_started',
    status: 'open',
    owner_id: '',
    due_date: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (risk) {
      setForm({
        title: risk.title || '',
        description: risk.description || '',
        category: risk.category || 'other',
        likelihood: risk.likelihood || 3,
        impact: risk.impact || 3,
        mitigation_plan: risk.mitigation_plan || '',
        mitigation_status: risk.mitigation_status || 'not_started',
        status: risk.status || 'open',
        owner_id: risk.owner_id || '',
        due_date: risk.due_date ? risk.due_date.split('T')[0] : ''
      });
    } else {
      setForm({
        title: '',
        description: '',
        category: 'other',
        likelihood: 3,
        impact: 3,
        mitigation_plan: '',
        mitigation_status: 'not_started',
        status: 'open',
        owner_id: '',
        due_date: ''
      });
    }
  }, [risk]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const severity = getSeverityColor(form.likelihood, form.impact);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl h-full overflow-y-auto">
        <div className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between bg-red-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <h2 className="text-lg font-semibold">{risk ? 'Edit Risk' : 'New Risk'}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Severity Preview */}
          <div className="p-4 rounded-lg text-center" style={{ backgroundColor: severity.bg }}>
            <div className="text-white font-bold text-lg">{severity.label}</div>
            <div className="text-white/80 text-sm">Score: {form.likelihood * form.impact}</div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="Risk title"
              data-testid="risk-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="Describe the risk..."
            />
          </div>

          {/* Category & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg capitalize"
              >
                {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg capitalize"
              >
                {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
          </div>

          {/* Likelihood & Impact */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Likelihood (1-5)
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={form.likelihood}
                onChange={(e) => setForm({ ...form, likelihood: parseInt(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Rare</span>
                <span className="font-bold">{form.likelihood}</span>
                <span>Certain</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Impact (1-5)
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={form.impact}
                onChange={(e) => setForm({ ...form, impact: parseInt(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Negligible</span>
                <span className="font-bold">{form.impact}</span>
                <span>Catastrophic</span>
              </div>
            </div>
          </div>

          {/* Mitigation Plan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mitigation Plan</label>
            <textarea
              value={form.mitigation_plan}
              onChange={(e) => setForm({ ...form, mitigation_plan: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="How will this risk be mitigated?"
            />
          </div>

          {/* Mitigation Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mitigation Status</label>
            <select
              value={form.mitigation_status}
              onChange={(e) => setForm({ ...form, mitigation_status: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg"
            >
              {MITIGATION_STATUSES.map(s => (
                <option key={s} value={s} className="capitalize">{s.replace('_', ' ')}</option>
              ))}
            </select>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            {risk && (
              <button
                type="button"
                onClick={() => onDelete(risk)}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              data-testid="save-risk-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {risk ? 'Update' : 'Create'} Risk
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Risk Matrix Page
const RiskMatrixPage = () => {
  const [loading, setLoading] = useState(true);
  const [matrixData, setMatrixData] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [filters, setFilters] = useState({ project_id: '', status: 'open' });
  const [projects, setProjects] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [exporting, setExporting] = useState(false);
  const matrixRef = useRef(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  }, []);

  // Fetch matrix data
  const fetchMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.project_id) params.append('project_id', filters.project_id);
      if (filters.status) params.append('status', filters.status);

      const response = await fetch(`${API_URL}/api/risks/matrix?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setMatrixData(data);
      } else {
        toast.error('Failed to load risk matrix');
      }
    } catch (err) {
      console.error('Failed to fetch matrix:', err);
      toast.error('Failed to load risk matrix');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filters]);

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API_URL}/api/projects`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setProjects(data.projects || []);
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      }
    };
    fetchProjects();
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  // Handle cell click
  const handleCellClick = (likelihood, impact, risks) => {
    setSelectedCell({ likelihood, impact, risks });
  };

  // Handle create/edit risk
  const handleSaveRisk = async (formData) => {
    try {
      const method = editingRisk ? 'PATCH' : 'POST';
      const url = editingRisk 
        ? `${API_URL}/api/risks/${editingRisk.id || editingRisk.risk_id}`
        : `${API_URL}/api/risks`;

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          project_id: filters.project_id || null
        })
      });

      if (response.ok) {
        toast.success(editingRisk ? 'Risk updated' : 'Risk created');
        setDrawerOpen(false);
        setEditingRisk(null);
        setSelectedCell(null);
        fetchMatrix();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save risk');
      }
    } catch (err) {
      console.error('Failed to save risk:', err);
      toast.error('Failed to save risk');
    }
  };

  // Handle delete risk
  const handleDeleteRisk = async (risk) => {
    if (!window.confirm('Are you sure you want to delete this risk?')) return;

    try {
      const response = await fetch(`${API_URL}/api/risks/${risk.id || risk.risk_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Risk deleted');
        setDrawerOpen(false);
        setEditingRisk(null);
        setSelectedCell(null);
        fetchMatrix();
      } else {
        toast.error('Failed to delete risk');
      }
    } catch (err) {
      console.error('Failed to delete risk:', err);
      toast.error('Failed to delete risk');
    }
  };

  // Export handlers
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.project_id) params.append('project_id', filters.project_id);
      if (filters.status) params.append('status', filters.status);

      const response = await fetch(`${API_URL}/api/risks/export/csv?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `risk_matrix_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('CSV exported');
      } else {
        toast.error('Failed to export CSV');
      }
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPNG = async () => {
    setExporting(true);
    try {
      // Use html2canvas to capture the matrix
      if (matrixRef.current && window.html2canvas) {
        const canvas = await window.html2canvas(matrixRef.current);
        const link = document.createElement('a');
        link.download = `risk_matrix_${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL();
        link.click();
        toast.success('PNG exported');
      } else {
        toast.info('PNG export requires html2canvas library');
      }
    } catch (err) {
      console.error('PNG export failed:', err);
      toast.error('Failed to export PNG');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !matrixData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <PageErrorBoundary pageName="Risk Matrix">
      <div className="min-h-screen bg-gray-50" data-testid="risk-matrix-page">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="w-7 h-7 text-red-600" />
                  Risk Heat Matrix
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Visualize and manage project risks by likelihood and impact
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Export Dropdown */}
                <div className="relative group">
                  <button 
                    className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
                    disabled={exporting}
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Export
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="absolute right-0 mt-1 w-40 bg-white border rounded-lg shadow-lg hidden group-hover:block z-10">
                    <button 
                      onClick={handleExportCSV}
                      className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-50 text-left"
                      data-testid="export-csv-btn"
                    >
                      <Table className="w-4 h-4" />
                      CSV
                    </button>
                    <button 
                      onClick={handleExportPNG}
                      className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-50 text-left"
                      data-testid="export-png-btn"
                    >
                      <Image className="w-4 h-4" />
                      PNG
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => { setEditingRisk(null); setDrawerOpen(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  data-testid="create-risk-btn"
                >
                  <Plus className="w-4 h-4" />
                  New Risk
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4 mt-4">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filters.project_id}
                onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">All Statuses</option>
                {STATUSES.map(s => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
              <button
                onClick={fetchMatrix}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Matrix */}
            <div className="lg:col-span-2">
              <WidgetErrorBoundary widgetName="Risk Matrix Grid">
                <div className="bg-white rounded-lg border p-6" ref={matrixRef}>
                  <h3 className="text-lg font-semibold mb-4">Risk Matrix (5×5)</h3>
                  
                  <div className="flex">
                    {/* Y-axis label */}
                    <div className="w-8 flex flex-col justify-center">
                      <div 
                        className="text-sm font-medium text-gray-500 transform -rotate-90 whitespace-nowrap"
                        style={{ transformOrigin: 'center center' }}
                      >
                        Likelihood →
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      {/* Matrix Grid */}
                      <div className="grid grid-cols-5 gap-2">
                        {[5, 4, 3, 2, 1].map(likelihood => (
                          [1, 2, 3, 4, 5].map(impact => {
                            const key = `${likelihood}_${impact}`;
                            const risks = matrixData?.matrix?.[key] || [];
                            return (
                              <MatrixCell
                                key={key}
                                likelihood={likelihood}
                                impact={impact}
                                risks={risks}
                                onClick={handleCellClick}
                                isSelected={selectedCell?.likelihood === likelihood && selectedCell?.impact === impact}
                              />
                            );
                          })
                        ))}
                      </div>
                      
                      {/* X-axis label */}
                      <div className="text-center mt-4">
                        <span className="text-sm font-medium text-gray-500">Impact →</span>
                      </div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t">
                    {Object.entries(SEVERITY_COLORS).map(([key, color]) => (
                      <div key={key} className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: color.bg }}
                        />
                        <span className="text-sm text-gray-600">{color.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </WidgetErrorBoundary>
            </div>

            {/* Side Panel */}
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-white rounded-lg border p-4">
                <h3 className="font-semibold mb-3">Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Risks</span>
                    <span className="font-semibold">{matrixData?.summary?.total || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-600">Critical</span>
                    <span className="font-semibold">{matrixData?.summary?.critical || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-600">High</span>
                    <span className="font-semibold">{matrixData?.summary?.high || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-yellow-600">Medium</span>
                    <span className="font-semibold">{matrixData?.summary?.medium || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-600">Low</span>
                    <span className="font-semibold">{matrixData?.summary?.low || 0}</span>
                  </div>
                </div>
              </div>

              {/* Selected Cell Risks */}
              {selectedCell && selectedCell.risks?.length > 0 && (
                <div className="bg-white rounded-lg border p-4">
                  <h3 className="font-semibold mb-3">
                    Risks at L{selectedCell.likelihood} × I{selectedCell.impact}
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      ({selectedCell.risks.length})
                    </span>
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedCell.risks.map((risk, idx) => (
                      <div 
                        key={idx}
                        className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                        onClick={() => { setEditingRisk(risk); setDrawerOpen(true); }}
                      >
                        <div className="font-medium text-sm">{risk.title}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                          <span className="capitalize">{risk.category}</span>
                          {risk.owner_name && (
                            <>
                              <span>•</span>
                              <span>{risk.owner_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Quick Tips</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Click a cell to view risks at that position</li>
                  <li>• Drag risks between cells to update severity</li>
                  <li>• Export to CSV for external reporting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Drawer */}
        <RiskDrawer
          isOpen={drawerOpen}
          onClose={() => { setDrawerOpen(false); setEditingRisk(null); }}
          risk={editingRisk}
          onSave={handleSaveRisk}
          onDelete={handleDeleteRisk}
        />
      </div>
    </PageErrorBoundary>
  );
};

export default RiskMatrixPage;
